import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import SystemLogin from './pages/SystemLogin';
import UserLogin from './pages/UserLogin';
import ResetMpin from './pages/ResetMpin';
import ChangePassword from './pages/ChangePassword';
import TenantManagement from './pages/TenantManagement';
import TenantSettings from './pages/TenantSettings';
import UserManagement from './pages/UserManagement';
import RoleManagement from './pages/RoleManagement';
import PermissionManagement from './pages/PermissionManagement';
import StaffCategoryManagement from './pages/StaffCategoryManagement';
import PatientManagement from './pages/PatientManagement';
import AmbulanceManagement from './pages/AmbulanceManagement';
import AmbulanceTypeManagement from './pages/AmbulanceTypeManagement';
import BookingManagement from './pages/BookingManagement';
import GpsDeviceManagement from './pages/GpsDeviceManagement';
import Reports from './pages/Reports';
import Profile from './pages/Profile';
import Tracking from './pages/Tracking';
import AttendanceManagement from './pages/AttendanceManagement';
import PatientVitalFieldsManagement from './pages/PatientVitalFieldsManagement';
import AreaManagement from './pages/AreaManagement';
import Fuel from './pages/Fuel';
import Settings from './pages/Settings';

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
          {/* Public Routes */}
          <Route path="/system" element={<SystemLogin />} />
          <Route path="/login" element={<UserLogin />} />
          <Route path="/reset-mpin" element={<ResetMpin />} />
          <Route path="/change-password" element={<ChangePassword />} />
          
          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute permission="tracking.view">
                <Tracking />
              </ProtectedRoute>
            }
          />

          {/* Tenant Management - admin or tenant.list */}
          <Route
            path="/dashboard/tenants"
            element={
              <ProtectedRoute permission="tenant.list">
                <TenantManagement />
              </ProtectedRoute>
            }
          />

          {/* Tenant Settings - admin or tenant.update */}
          <Route
            path="/dashboard/tenant-settings"
            element={
              <ProtectedRoute permission="tenant.update">
                <TenantSettings />
              </ProtectedRoute>
            }
          />

          {/* Role Management - admin or role.list */}
          <Route
            path="/dashboard/roles"
            element={
              <ProtectedRoute permission="role.list">
                <RoleManagement />
              </ProtectedRoute>
            }
          />

          {/* Permission Management - admin or permission.list */}
          <Route
            path="/dashboard/permissions"
            element={
              <ProtectedRoute permission="permission.list">
                <PermissionManagement />
              </ProtectedRoute>
            }
          />

          {/* User/Staff Management - admin or user.list */}
          <Route
            path="/dashboard/users"
            element={
              <ProtectedRoute permission="user.list">
                <UserManagement />
              </ProtectedRoute>
            }
          />

          {/* Staff Category Management - admin or staffcategory.list */}
          <Route
            path="/dashboard/staff-categories"
            element={
              <ProtectedRoute permission="staffcategory.list">
                <StaffCategoryManagement />
              </ProtectedRoute>
            }
          />

          {/* Patient Management - admin or patient.list */}
          <Route
            path="/dashboard/patients"
            element={
              <ProtectedRoute permission="patient.list">
                <PatientManagement />
              </ProtectedRoute>
            }
          />

          {/* Ambulance Management - admin or ambulance.list */}
          <Route
            path="/dashboard/ambulances"
            element={
              <ProtectedRoute permission="ambulance.list">
                <AmbulanceManagement />
              </ProtectedRoute>
            }
          />

          {/* Ambulance Type Management - admin or ambulancetype.list */}
          <Route
            path="/dashboard/ambulance-types"
            element={
              <ProtectedRoute permission="ambulancetype.list">
                <AmbulanceTypeManagement />
              </ProtectedRoute>
            }
          />

          {/* Booking Management - admin or booking.list (all) or booking.get (own) */}
          <Route
            path="/dashboard/bookings"
            element={
              <ProtectedRoute permissions={['booking.list', 'booking.get']}>
                <BookingManagement />
              </ProtectedRoute>
            }
          />

          {/* GPS Device Management - admin or gpsdevice.list */}
          <Route
            path="/dashboard/gps-devices"
            element={
              <ProtectedRoute permission="gpsdevice.list">
                <GpsDeviceManagement />
              </ProtectedRoute>
            }
          />

          {/* Reports - admin or report.read */}
          <Route
            path="/dashboard/reports"
            element={
              <ProtectedRoute permission="report.read">
                <Reports />
              </ProtectedRoute>
            }
          />


          {/* Profile - admin or profile.view */}
          <Route
            path="/dashboard/profile"
            element={
              <ProtectedRoute permission="profile.view">
                <Profile />
              </ProtectedRoute>
            }
          />

          {/* Attendance Management - admin or attendance.list */}
          <Route
            path="/dashboard/attendance"
            element={
              <ProtectedRoute permission="attendance.list">
                <AttendanceManagement />
              </ProtectedRoute>
            }
          />

          {/* Patient Vital Fields Management - admin or patientvitalfield.list */}
          <Route
            path="/dashboard/patient-vital-fields"
            element={
              <ProtectedRoute permission="patientvitalfield.list">
                <PatientVitalFieldsManagement />
              </ProtectedRoute>
            }
          />

          {/* Area Management - admin or area.list */}
          <Route
            path="/dashboard/areas"
            element={
              <ProtectedRoute permission="area.list">
                <AreaManagement />
              </ProtectedRoute>
            }
          />

          {/* Fuel Management - admin or fuel.view */}
          <Route
            path="/dashboard/fuel"
            element={
              <ProtectedRoute permission="fuel.view">
                <Fuel />
              </ProtectedRoute>
            }
          />

          {/* Settings - Combined settings page */}
          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute permission="ambulancetype.list">
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;

